import {
  type AttendanceVerificationMethod,
  type AttendanceVerificationMethodConfig,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type CbhAppInfo, useAppInfo } from "@src/appV2/lib/index";
import type { ShiftStateMetadata } from "@src/appV2/Shifts/Shift/ShiftState/types";
import type { Shift } from "@src/appV2/Shifts/Shift/types";
import { isEnabledForAppVersionAndOta } from "@src/appV2/Shifts/utils/isSolveUnpaidBreaksFeatureEnabled";
import { isNil } from "lodash";
import { useCallback, useMemo, useRef } from "react";

import { useLegacyAttendanceVerificationMethods } from "./useLegacyAttendanceTrackingMethods";
import { useNewAttendanceVerificationMethods } from "./useNewAttendanceVerificationMethods";

interface MethodVersionConfig {
  minimalOtaVersion: string | number;
  minimalStoreVersion: string;
  state: "enabled" | "disabled";
}

type MethodsVersions = Partial<Record<string, MethodVersionConfig>>;

function filterMethodsByVersion(
  methods: AttendanceVerificationMethodConfig[],
  methodsConfiguration: MethodsVersions | undefined,
  appInfo: CbhAppInfo | undefined
): AttendanceVerificationMethodConfig[] {
  if (!isDefined(methodsConfiguration)) {
    return methods;
  }

  return methods.filter((method) => {
    const methodVersion = methodsConfiguration[method.type];
    // If method is not in methodsConfiguration, disable it
    if (!isDefined(methodVersion)) {
      return false;
    }

    if (methodVersion.state === "disabled") {
      return false;
    }

    return isEnabledForAppVersionAndOta({
      minimalOtaVersion: methodVersion.minimalOtaVersion,
      minimalStoreVersion: methodVersion.minimalStoreVersion,
      appInfo,
    });
  });
}

interface UseAttendanceVerificationMethodParams {
  shift?: Pick<Shift, "_id" | "nfcTag" | "nfcTagRequests" | "facilityId" | "facility">;
  shiftStateMetadata?: ShiftStateMetadata;
}

interface UseAttendanceVerificationMethodResult {
  isMethodEnabled: (method: AttendanceVerificationMethod) => boolean;
  getMethodConfig: <T extends AttendanceVerificationMethodConfig>(
    method: AttendanceVerificationMethod
  ) => T | undefined;
  attendanceVerificationMethods: AttendanceVerificationMethodConfig[];
  externalTimeclockCredentials: {
    badgeNumber: string | undefined;
    pin: string | undefined;
  };
}

export function useAttendanceVerificationMethod(
  params: UseAttendanceVerificationMethodParams
): UseAttendanceVerificationMethodResult {
  const { shift, shiftStateMetadata } = params;

  const { appInfo } = useAppInfo();

  const attendanceVerificationMethodsRollout = useCbhFlag(
    CbhFeatureFlag.ATTENDANCE_TRACKING_METHODS_ROLLOUT,
    {
      defaultValue: {
        state: "off" as const,
        minimalOtaVersion: "*",
        minimalStoreVersion: "*",
        methodsConfiguration: undefined,
      },
    }
  );

  const hasSentMismatchEventRef = useRef<string | null>(null);

  const legacyMethods = useLegacyAttendanceVerificationMethods({
    shift,
    shiftStateMetadata,
  });
  const newMethods = useNewAttendanceVerificationMethods({ shiftStateMetadata });

  const { state, minimalOtaVersion, minimalStoreVersion, methodsConfiguration } =
    attendanceVerificationMethodsRollout;

  const isVersionValid = isEnabledForAppVersionAndOta({
    minimalOtaVersion,
    minimalStoreVersion,
    appInfo,
  });

  const shouldUseNewConfiguration = state === "on" && isVersionValid;

  // Filter methods based on methodsConfiguration configuration (only for new configuration)
  const enabledMethods = useMemo(() => {
    if (!shouldUseNewConfiguration) {
      return legacyMethods;
    }

    return filterMethodsByVersion(newMethods, methodsConfiguration, appInfo);
  }, [shouldUseNewConfiguration, legacyMethods, newMethods, methodsConfiguration, appInfo]);

  const hasMismatch = legacyMethods.some(
    (method, index) =>
      isNil(newMethods[index]) ||
      method.type !== newMethods[index].type ||
      newMethods[index].isEnabled !== method.isEnabled
  );
  const shouldSendMismatchEvent =
    isVersionValid &&
    isDefined(shift) &&
    isDefined(shiftStateMetadata) &&
    hasMismatch &&
    (state === "dry-run" || state === "on") &&
    hasSentMismatchEventRef.current !== shift?._id;

  if (shouldSendMismatchEvent) {
    hasSentMismatchEventRef.current = shift._id;
    logEvent(APP_V2_USER_EVENTS.ATTENDANCE_TRACKING_METHODS_CONFIGURATION_MISMATCH, {
      isVersionValid,
      shiftId: shift._id,
      workplaceId: shift.facilityId,
      featureFlagState: state,
      legacyMethods,
      newMethods,
      enabledMethods,
      attendanceVerificationMethods: shiftStateMetadata.attendanceVerificationMethods,
    });
  }

  const externalTimeclockCredentials = {
    badgeNumber: shiftStateMetadata?.externalTimeclockCredentials?.badgeNumber,
    pin: shiftStateMetadata?.externalTimeclockCredentials?.pin,
  };

  const isMethodEnabled = useCallback(
    (method: AttendanceVerificationMethod): boolean => {
      return enabledMethods.some(
        (methodConfig) => methodConfig.type === method && methodConfig.isEnabled
      );
    },
    [enabledMethods]
  );

  const getMethodConfig = useCallback(
    <T extends AttendanceVerificationMethodConfig>(
      method: AttendanceVerificationMethod
    ): T | undefined => {
      return enabledMethods.find((methodConfig) => methodConfig.type === method) as T | undefined;
    },
    [enabledMethods]
  );

  return {
    attendanceVerificationMethods: enabledMethods,
    isMethodEnabled,
    getMethodConfig,
    externalTimeclockCredentials,
  };
}
